import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../auth/AuthContext";
import { SearchBar } from "../components/SearchBar";
import { Pagination } from "../components/Pagination";
import SelectScenarios from "../components/SelectScenarios";

const TASKS_MONITOR_STATE_KEY = "admin_tasks_monitor_state_v1";

function readTasksMonitorState() {
  try {
    const raw = localStorage.getItem(TASKS_MONITOR_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

function statusClasses(status) {
  const s = String(status || "").toLowerCase();
  if (s === "done") return "bg-emerald-100 text-emerald-800";
  if (s === "failed" || s === "error") return "bg-red-100 text-red-800";
  if (s === "todo" || s === "pending") return "bg-amber-100 text-amber-800";
  return "bg-slate-200 text-slate-700";
}

function shorten(text, max = 80) {
  const value = String(text || "");
  if (value.length <= max) return value;
  return `${value.slice(0, max)}...`;
}

function validateJsonText(text) {
  const raw = String(text || "").trim();
  if (!raw) return { valid: true, empty: true, error: null };
  try {
    const parsed = JSON.parse(raw);
    return { valid: true, empty: false, error: null, parsed };
  } catch (err) {
    return {
      valid: false,
      empty: false,
      error: err?.message || "Invalid JSON",
    };
  }
}

function formatColvalueForEditor(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return String(value || "");
  }
}

export default function TasksAdmin() {
  const { authFetch } = useAuth();
  const storedState = useMemo(() => readTasksMonitorState(), []);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchInput, setSearchInput] = useState(() =>
    String(storedState?.searchInput || ""),
  );
  const [search, setSearch] = useState(() => String(storedState?.search || ""));
  const [colnameFilter, setColnameFilter] = useState(() =>
    String(storedState?.colnameFilter || ""),
  );

  const [page, setPage] = useState(() => {
    const candidate = Number(storedState?.page);
    return Number.isInteger(candidate) && candidate > 0 ? candidate : 1;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);
  const [refreshTick, setRefreshTick] = useState(0);
  const [colnameOptions, setColnameOptions] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [editingValues, setEditingValues] = useState({});
  const [workingId, setWorkingId] = useState(null);

  const jsonState = useMemo(
    () => validateJsonText(editingValues?.colvalue),
    [editingValues?.colvalue],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    try {
      localStorage.setItem(
        TASKS_MONITOR_STATE_KEY,
        JSON.stringify({
          searchInput,
          search,
          page,
          colnameFilter,
        }),
      );
    } catch {
      // ignore storage errors
    }
  }, [searchInput, search, page, colnameFilter]);

  useEffect(() => {
    const loadColnameOptions = async () => {
      try {
        const response = await authFetch("/api/auth/admin/tasks/colnames");
        if (!response.ok) return;
        const data = await response.json();
        const options = Array.isArray(data?.colnames)
          ? data.colnames
              .map((x) => String(x || "").trim())
              .filter(Boolean)
          : [];
        setColnameOptions(options);
      } catch {
        setColnameOptions([]);
      }
    };

    loadColnameOptions();
  }, [authFetch]);

  useEffect(() => {
    if (!colnameFilter) return;
    if (colnameOptions.includes(colnameFilter)) return;
    setColnameFilter("");
  }, [colnameFilter, colnameOptions]);

  useEffect(() => {
    const loadTasks = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
        });
        if (search) params.set("search", search);
        if (colnameFilter) params.set("colname", colnameFilter);

        const response = await authFetch(`/api/auth/admin/tasks?${params.toString()}`);
        if (!response.ok) {
          let msg = "Error loading tasks";
          try {
            const data = await response.json();
            msg = data?.error || msg;
          } catch {}
          throw new Error(msg);
        }

        const data = await response.json();
        const rows = Array.isArray(data?.tasks) ? data.tasks : [];
        const pagination = data?.pagination || {};

        setTasks(rows);
        setTotal(Number(pagination.total) || 0);
        setTotalPages(Math.max(1, Number(pagination.totalPages) || 1));
      } catch (err) {
        console.error("Load admin tasks error:", err);
        setError(err?.message || "Error loading tasks");
        setTasks([]);
      } finally {
        setLoading(false);
      }
    };

    loadTasks();
  }, [authFetch, page, limit, search, colnameFilter, refreshTick]);

  const rows = useMemo(() => tasks, [tasks]);

  const closeEditModal = () => {
    setEditingId(null);
    setEditingValues({});
  };

  const beginEdit = (task) => {
    setEditingId(task.id);
    setEditingValues({
      task_id: task.task_id ?? "",
      country: task.country ?? "",
      name: task.name ?? "",
      colname: task.colname ?? "",
      colvalue: formatColvalueForEditor(task.colvalue),
      status: task.status ?? "",
      onpc: task.onpc ?? "",
    });
  };

  const handleSave = async (id) => {
    if (!jsonState.valid) {
      await Swal.fire({
        icon: "warning",
        title: "Invalid JSON",
        text: "COLVALUE contains invalid JSON. Fix it before saving.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    try {
      setWorkingId(id);

      const payload = {
        task_id: editingValues.task_id === "" ? null : Number(editingValues.task_id),
        country: String(editingValues.country || ""),
        name: String(editingValues.name || ""),
        colname: String(editingValues.colname || ""),
        colvalue: String(editingValues.colvalue || ""),
        status: String(editingValues.status || ""),
        onpc: editingValues.onpc === "" ? null : Number(editingValues.onpc),
      };

      const response = await authFetch(`/api/auth/admin/tasks/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let msg = "Error updating task";
        try {
          const data = await response.json();
          msg = data?.error || msg;
        } catch {}
        throw new Error(msg);
      }

      const data = await response.json();
      const updated = data?.task;
      if (updated) {
        setTasks((prev) => prev.map((t) => (t.id === id ? updated : t)));
      }
      closeEditModal();

      await Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Task updated successfully.",
        confirmButtonColor: "#3085d6",
      });
    } catch (err) {
      console.error("Update task error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Error updating task",
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setWorkingId(null);
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Confirm",
      text: `Delete task #${id}?`,
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      confirmButtonColor: "#d33",
    });

    if (!confirm.isConfirmed) return;

    try {
      setWorkingId(id);
      const response = await authFetch(`/api/auth/admin/tasks/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        let msg = "Error deleting task";
        try {
          const data = await response.json();
          msg = data?.error || msg;
        } catch {}
        throw new Error(msg);
      }

      setTasks((prev) => prev.filter((t) => t.id !== id));
      setTotal((prev) => Math.max(0, prev - 1));

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Task deleted successfully.",
        confirmButtonColor: "#3085d6",
      });
    } catch (err) {
      console.error("Delete task error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Error deleting task",
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setWorkingId(null);
    }
  };

  const handleFormatJson = async () => {
    const current = String(editingValues?.colvalue || "").trim();
    if (!current) return;
    try {
      const formatted = JSON.stringify(JSON.parse(current), null, 2);
      setEditingValues((prev) => ({ ...prev, colvalue: formatted }));
    } catch {
      await Swal.fire({
        icon: "warning",
        title: "Invalid JSON",
        text: "Cannot format because COLVALUE is not valid JSON.",
        confirmButtonColor: "#3085d6",
      });
    }
  };

  const handleMinifyJson = async () => {
    const current = String(editingValues?.colvalue || "").trim();
    if (!current) return;
    try {
      const minified = JSON.stringify(JSON.parse(current));
      setEditingValues((prev) => ({ ...prev, colvalue: minified }));
    } catch {
      await Swal.fire({
        icon: "warning",
        title: "Invalid JSON",
        text: "Cannot minify because COLVALUE is not valid JSON.",
        confirmButtonColor: "#3085d6",
      });
    }
  };

  if (loading) {
    return (
      <div className="w-full p-6 flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading tasks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-red-800 font-semibold mb-2">Error</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-4 md:p-6 font-montserrat space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Tasks Monitor</h2>
          <p className="text-gray-600">Review all tasks submitted to tasktodo.</p>
        </div>
        <button
          type="button"
          onClick={() => setRefreshTick((n) => n + 1)}
          className="inline-flex w-full sm:w-auto justify-center items-center gap-2 rounded-lg bg-[#03203d] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#05407b]"
        >
          <span aria-hidden="true">↻</span>
          Refresh
        </button>
      </div>

      <div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="w-full lg:flex-1">
              <SearchBar
                value={searchInput}
                onChange={setSearchInput}
                placeholder="Search by id, task id, user, country, name, colname or status..."
              />
            </div>
            <SelectScenarios
              value={colnameFilter}
              onChange={(next) => {
                setColnameFilter(String(next || ""));
                setPage(1);
              }}
              options={[
                { value: "", label: "All colnames" },
                ...colnameOptions.map((colname) => ({ value: colname, label: colname })),
              ]}
              placeholder="All colnames"
              className="w-full lg:w-64"
            />
          </div>
        </div>
      </div>

      <div className="min-h-0">
        <div className="md:hidden space-y-3">
          {rows.map((task) => (
            <div key={task.id} className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-gray-900">Task #{task.id}</p>
                <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${statusClasses(task.status)}`}>
                  {task.status || "-"}
                </span>
              </div>

              <div className="mt-3 grid grid-cols-[auto,1fr] gap-x-3 gap-y-2 text-sm">
                <p className="text-gray-500">Task ID</p>
                <p className="text-gray-800">{task.task_id ?? "-"}</p>

                <p className="text-gray-500">Country</p>
                <p className="text-gray-800">{task.country || "-"}</p>

                <p className="text-gray-500">Name</p>
                <p className="text-gray-800">{task.name || "-"}</p>

                <p className="text-gray-500">Colname</p>
                <p className="text-gray-800">{task.colname || "-"}</p>

                <p className="text-gray-500">Created</p>
                <p className="text-gray-800 break-all">{task.created_at || "-"}</p>
              </div>

              <div className="mt-3 rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700" title={task.colvalue || ""}>
                {shorten(task.colvalue, 70) || "-"}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  onClick={() => beginEdit(task)}
                  className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 w-full sm:w-24 text-center"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(task.id)}
                  disabled={workingId === task.id}
                  className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 w-full sm:w-24 text-center"
                >
                  {workingId === task.id ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          ))}

          {rows.length === 0 && (
            <div className="px-6 py-12 text-center text-gray-500 bg-white rounded-lg shadow-sm">
              <p className="text-lg">No tasks found</p>
            </div>
          )}
        </div>

        <div className="hidden md:block bg-white rounded-lg shadow-sm h-full overflow-hidden">
          <div className="h-full overflow-x-auto overflow-y-auto">
          <table className="w-full min-w-[980px] lg:min-w-[1120px]">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">ID</th>
                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Task ID</th>
                <th className="hidden lg:table-cell px-4 py-2 text-left text-sm font-semibold text-gray-700">User</th>
                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Country</th>
                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Name</th>
                <th className="hidden lg:table-cell px-4 py-2 text-left text-sm font-semibold text-gray-700">Colname</th>
                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Created</th>
                <th className="hidden xl:table-cell px-4 py-2 text-left text-sm font-semibold text-gray-700">onpc</th>
                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Colvalue</th>
                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((task) => (
                <tr key={task.id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-2 text-sm font-semibold text-gray-900">{task.id}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{task.task_id ?? "-"}</td>
                  <td className="hidden lg:table-cell px-4 py-2 text-sm text-gray-700">{task.user_id || "-"}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{task.country || "-"}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{task.name || "-"}</td>
                  <td className="hidden lg:table-cell px-4 py-2 text-sm text-gray-700">{task.colname || "-"}</td>
                  <td className="px-4 py-2 text-sm">
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${statusClasses(task.status)}`}>
                      {task.status || "-"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">{task.created_at || "-"}</td>
                  <td className="hidden xl:table-cell px-4 py-2 text-sm text-gray-700">{task.onpc ?? "-"}</td>
                  <td className="px-4 py-2 text-sm text-gray-700 max-w-[300px]" title={task.colvalue || ""}>
                    <span className="break-all">{shorten(task.colvalue, 55) || "-"}</span>
                  </td>
                  <td className="px-4 py-2 text-sm whitespace-nowrap">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => beginEdit(task)}
                        className="px-2.5 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 w-20 text-center"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(task.id)}
                        disabled={workingId === task.id}
                        className="px-2.5 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 w-20 text-center"
                      >
                        {workingId === task.id ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {rows.length === 0 && (
            <div className="px-6 py-12 text-center text-gray-500">
              <p className="text-lg">No tasks found</p>
            </div>
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <div>
          <div className="bg-white rounded-lg shadow-sm p-4 overflow-x-auto">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        </div>
      )}

      <div>
        <div className="text-sm text-gray-600 text-center bg-blue-50 rounded-lg p-4">
          <p>
            Showing <strong>{rows.length}</strong> of <strong>{total}</strong> task(s)
            {search && <span className="break-all"> for "{search}"</span>}
            {colnameFilter && (
              <span>
                {" "}
                in <strong>{colnameFilter}</strong>
              </span>
            )}
          </p>
        </div>
      </div>

      {editingId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 md:p-4">
          <div className="w-full max-w-3xl rounded-xl bg-white shadow-xl max-h-[92dvh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Edit Task #{editingId}</h2>
              <button
                onClick={closeEditModal}
                className="rounded-md px-2 py-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
              >
                ✕
              </button>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Task ID">
                <input
                  type="number"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={editingValues.task_id}
                  onChange={(e) => setEditingValues((prev) => ({ ...prev, task_id: e.target.value }))}
                />
              </Field>

              <Field label="Status">
                <input
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={editingValues.status}
                  onChange={(e) => setEditingValues((prev) => ({ ...prev, status: e.target.value }))}
                />
              </Field>

              <Field label="Country">
                <input
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={editingValues.country}
                  onChange={(e) => setEditingValues((prev) => ({ ...prev, country: e.target.value }))}
                />
              </Field>

              <Field label="onpc">
                <input
                  type="number"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={editingValues.onpc}
                  onChange={(e) => setEditingValues((prev) => ({ ...prev, onpc: e.target.value }))}
                />
              </Field>

              <Field label="Name">
                <input
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={editingValues.name}
                  onChange={(e) => setEditingValues((prev) => ({ ...prev, name: e.target.value }))}
                />
              </Field>

              <Field label="Colname">
                <input
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={editingValues.colname}
                  onChange={(e) => setEditingValues((prev) => ({ ...prev, colname: e.target.value }))}
                />
              </Field>

              <div className="md:col-span-2">
                <Field label="Colvalue">
                  <JsonEditor
                    value={editingValues.colvalue}
                    onChange={(next) => setEditingValues((prev) => ({ ...prev, colvalue: next }))}
                    isValid={jsonState.valid}
                    error={jsonState.error}
                    onFormat={handleFormatJson}
                    onMinify={handleMinifyJson}
                  />
                </Field>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-4 flex items-center justify-end gap-2">
              <button
                onClick={closeEditModal}
                disabled={workingId === editingId}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded hover:bg-slate-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSave(editingId)}
                disabled={workingId === editingId || !jsonState.valid}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                {workingId === editingId ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      {children}
    </label>
  );
}

function JsonEditor({ value, onChange, isValid, error, onFormat, onMinify }) {
  const lineCount = Math.max(1, String(value || "").split("\n").length);
  const lineNumbers = Array.from({ length: lineCount }, (_, i) => i + 1).join("\n");

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onFormat}
          className="rounded bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700"
        >
          Format JSON
        </button>
        <button
          type="button"
          onClick={onMinify}
          className="rounded bg-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-300"
        >
          Minify
        </button>
        <span
          className={`text-xs font-medium ${isValid ? "text-emerald-700" : "text-red-700"}`}
        >
          {isValid ? "Valid JSON" : "Invalid JSON"}
        </span>
      </div>

      <div className="grid grid-cols-[auto,1fr] overflow-hidden rounded border border-slate-300 bg-white">
        <pre className="m-0 select-none border-r border-slate-200 bg-slate-50 px-2 py-2 text-right text-xs text-slate-400 font-mono leading-5">
          {lineNumbers}
        </pre>
        <textarea
          className="w-full min-h-48 resize-y border-0 px-3 py-2 text-sm font-mono leading-5 outline-none"
          spellCheck={false}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder='{"example": true}'
        />
      </div>

      {!isValid && (
        <p className="text-xs text-red-700">{error}</p>
      )}
    </div>
  );
}
